import * as React from "react";
import { useLocation } from "react-router-dom";
import { DefaultTheme, withTheme } from "styled-components";

import bloodLgImg from "../assets/banner/blood-bikes-lg.jpg";
import bloodLgImg2x from "../assets/banner/blood-bikes-lg@2x.jpg";
import bloodImg from "../assets/banner/blood-bikes.jpg";
import bloodImg2x from "../assets/banner/blood-bikes@2x.jpg";

import easterLgImg from "../assets/banner/easter-show-lg.jpg";
import easterLgImg2x from "../assets/banner/easter-show-lg@2x.jpg";
import easterImg from "../assets/banner/easter-show.jpg";
import easterImg2x from "../assets/banner/easter-show@2x.jpg";

import doctorLgImg from "../assets/banner/royal-flying-doctor-service-lg.jpg";
import doctorLgImg2x from "../assets/banner/royal-flying-doctor-service-lg@2x.jpg";
import doctorImg from "../assets/banner/royal-flying-doctor-service.jpg";
import doctorImg2x from "../assets/banner/royal-flying-doctor-service@2x.jpg";

import { CustomError } from "@skv/core";
import { opportunityStore, organisationStore } from "@skv/state";
import rescueLgImg from "../assets/banner/volunteer-rescue-association-lg.jpg";
import rescueLgImg2x from "../assets/banner/volunteer-rescue-association-lg@2x.jpg";
import rescueImg from "../assets/banner/volunteer-rescue-association.jpg";
import rescueImg2x from "../assets/banner/volunteer-rescue-association@2x.jpg";

interface BannerProps {
  theme: DefaultTheme;
}

const images = [
  {
    img: rescueImg,
    img2x: rescueImg2x,
    imgLg: rescueLgImg,
    imgLg2x: rescueLgImg2x,
  },
  {
    img: bloodImg,
    img2x: bloodImg2x,
    imgLg: bloodLgImg,
    imgLg2x: bloodLgImg2x,
  },
  {
    img: easterImg,
    img2x: easterImg2x,
    imgLg: easterLgImg,
    imgLg2x: easterLgImg2x,
  },
  {
    img: doctorImg,
    img2x: doctorImg2x,
    imgLg: doctorLgImg,
    imgLg2x: doctorLgImg2x,
  },
];

const getBannerImage = () => {
  return images[Math.floor(Math.random() * images.length)];
};

const getOrgId = (pathname: string) => {
  const match = pathname.match(/\/volunteering-organisations?\/(\d+)\/\w+/);
  return match ? match[1] : "-1";
};

const getOppId = (pathname: string) => {
  const match = pathname.match(/\/volunteering?\/(\d+)\/\w+/);
  return match ? match[1] : "-1";
};

const shouldShowBanner = (
  pathname: string,
  opportunityError: CustomError<unknown>,
  organisationError: CustomError<unknown>
) => {
  switch (true) {
    case !!pathname.match(/\/volunteering-organisations?\/(\d+)\/\w+/):
      return !organisationError;
    case !!pathname.match(/\/volunteering?\/(\d+)\/\w+/):
      return !opportunityError;
    default:
      return true;
  }
};

const BaseBanner: React.FC<BannerProps> = ({ theme }) => {
  const location = useLocation();
  const orgId = getOrgId(location.pathname);
  const oppId = getOppId(location.pathname);

  const { error: opportunityError } =
    opportunityStore?.hooks?.useOpportunity(oppId);
  const { error: organisationError } =
    organisationStore?.hooks?.useOrganisation(orgId);

  const showBanner = shouldShowBanner(
    location.pathname,
    opportunityError,
    organisationError
  );

  // Memoize the heroImg based on the route
  const heroImg = React.useMemo(() => {
    return getBannerImage();
  }, [location.pathname]);

  if (!showBanner) {
    return null;
  }

  return (
    <picture
      className="d-block"
      css={`
        width: 100%;
        height: 158px;
        @media (min-width: ${theme.mdBreakpoint}) {
          height: 295px;
        }
      `}
    >
      <source
        srcSet={`${heroImg.imgLg}, ${heroImg.imgLg2x} 2x`}
        media={`(min-width: ${theme.mdBreakpoint})`}
      />
      <img
        css={`
          position: relative;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: -1;
        `}
        srcSet={`${heroImg.img}, ${heroImg.img2x} 2x`}
        src={heroImg.img}
        alt=""
      />
    </picture>
  );
};

export const Banner = withTheme(BaseBanner);
